import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-svg-canvas-preview',
  standalone: true,
  imports: [],
  templateUrl: './svg-canvas-preview.component.html',
  styleUrl: './svg-canvas-preview.component.css',
})
export class SvgCanvasPreviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() filePath!: string;
  @Input() isRefresh: boolean = false;
  @Input() subscribedPackage?: number;
  /** Emits the `filePath` that finished loading (or failed). */
  @Output() loadComplete = new EventEmitter<string>();
  private cssSize = 360;
  waterMrk: string =
    '../../../../../../../../assets/theme/images/logo/watermark-hd.svg';
  @ViewChild('canvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;
  private readonly isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.setCanvasSize();
    this.loadSvgToCanvas(this.filePath);
    window.addEventListener('resize', this.handleResize);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isBrowser) {
      return;
    }
    if (changes && this.isRefresh) {
      this.resetCanvas();
      this.loadSvgToCanvas(this.filePath);
    }
  }

  resetCanvas(): void {
    if (!this.isBrowser || !this.canvasRef?.nativeElement) {
      return;
    }
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  setCanvasSize(): void {
    if (!this.isBrowser || !this.canvasRef?.nativeElement) {
      return;
    }
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');

    const dpr = window.devicePixelRatio || 1;

    this.cssSize = window.innerWidth <= 1280 ? 280 : 360;

    canvas.width = this.cssSize * dpr;
    canvas.height = this.cssSize * dpr;

    canvas.style.width = `${this.cssSize}px`;
    canvas.style.height = `${this.cssSize}px`;

    ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  handleResize = () => {
    this.setCanvasSize();
    this.loadSvgToCanvas(this.filePath);
  };

  loadSvgToCanvas(svgPath: string): void {
    if (!this.isBrowser || !this.canvasRef?.nativeElement || !svgPath) {
      return;
    }
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      const mainImage = new Image();
      mainImage.src = svgPath;
      const handleDone = (path: string) => this.loadComplete.emit(path);

      mainImage.onerror = () => handleDone(svgPath);
      mainImage.onload = () => {
        requestAnimationFrame(() => {
          ctx.drawImage(mainImage, 0, 0, this.cssSize, this.cssSize);
          // Main art is ready; don’t block on async watermark
          handleDone(svgPath);

          if (this.subscribedPackage !== 1) {
            const watermarkImage = new Image();
            watermarkImage.src = this.waterMrk;

            watermarkImage.onload = () => {
              const ctxWidth = this.cssSize;
              const ctxHeight = this.cssSize;

              ctx.save();
              ctx.globalAlpha = 0.8;

              ctx.drawImage(watermarkImage, 0, 0, ctxWidth, ctxHeight);

              ctx.restore();
            };
          }
        });
      };
    }
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) {
      return;
    }
    window.removeEventListener('resize', this.handleResize);
    this.resetCanvas();
  }
}
